export interface BlendedCardPricingEntry {
  basis_points?: number | null;
  fixed_rate?: number | null;
}

export interface CardPricingWithBlendedRates {
  premium_basis_points?: number | null;
  premium_fixed_rate?: number | null;
  blended_card_pricing?: Record<string, BlendedCardPricingEntry>;
}

const UPPERCASE_CARD_TYPE_WORDS = new Set(['JCB']);

export function formatCardType(cardType: string): string {
  return cardType
    .split('_')
    .map(word => {
      if (UPPERCASE_CARD_TYPE_WORDS.has(word)) {
        return word.toUpperCase();
      }
      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');
}

// Always returns an explicit fee string (e.g. "0% + $0.00") — never empty.
// Do not use the return value for zero-fee filtering; use hasBlendedCardPricing
// to decide section visibility instead.
export function formatCardTransactionFee(
  basisPoints: number | string | null | undefined,
  fixedRate: number | string | null | undefined,
  currencySymbol = '$',
): string {
  const numericBasisPoints = Number(basisPoints) || 0;
  const numericFixedRate = Number(fixedRate) || 0;

  return `${numericBasisPoints / 100}% + ${currencySymbol}${(numericFixedRate / 100).toFixed(2)}`;
}

export function hasBlendedCardPricing(
  cardPricing: CardPricingWithBlendedRates | null | undefined,
): boolean {
  if (!cardPricing) {
    return false;
  }

  const hasPremiumBasisPoints = Number(cardPricing.premium_basis_points) > 0;
  const hasPremiumFixedRate = Number(cardPricing.premium_fixed_rate) > 0;
  if (hasPremiumBasisPoints || hasPremiumFixedRate) {
    return true;
  }

  const blendedCardPricing = cardPricing.blended_card_pricing || {};
  return Object.keys(blendedCardPricing).length > 0;
}

export function getBlendedCardPricingDetails(
  cardPricing: CardPricingWithBlendedRates | null | undefined,
  currencySymbol = '$',
): string[] {
  if (!cardPricing) {
    return [];
  }

  const hasPremiumBasisPoints = Number(cardPricing.premium_basis_points) > 0;
  const hasPremiumFixedRate = Number(cardPricing.premium_fixed_rate) > 0;
  if (hasPremiumBasisPoints || hasPremiumFixedRate) {
    return [
      formatCardTransactionFee(
        cardPricing.premium_basis_points,
        cardPricing.premium_fixed_rate,
        currencySymbol,
      ),
    ];
  }

  const blendedCardPricing = cardPricing.blended_card_pricing || {};
  return Object.entries(blendedCardPricing).map(([cardType, pricing]) => {
    const cardTypeLabel = formatCardType(cardType);
    const fee = formatCardTransactionFee(
      pricing?.basis_points,
      pricing?.fixed_rate,
      currencySymbol,
    );
    return `${cardTypeLabel}: ${fee}`;
  });
}
